/**
 * vehicleAuditEvents — rótulos e detalhamento dos eventos de auditoria do veículo
 * compartilhados pela aba Histórico e pela Timeline (evita divergência de textos).
 *
 * @version 1.1.0
 * RELEASE: 2026-10-09 v1.1.0 — BR-OS-028: eventos da Ordem de Serviço (service_order_*).
 * RELEASE: 2026-10-02 v1.0.0 — Status do veículo, vistorias, cobranças/assinaturas e
 *          progresso de tarefas vinculadas.
 */

/** Rótulos dos novos eventos gravados pelos gatilhos de auditoria. */
export const VEHICLE_AUDIT_LABELS: Record<string, string> = {
  vehicle_created: 'Veículo cadastrado',
  vehicle_updated: 'Dados do veículo atualizados',
  vehicle_status_changed: 'Status do veículo alterado',
  inspection_created: 'Vistoria criada',
  inspection_status_changed: 'Situação da vistoria alterada',
  charge_linked: 'Cobrança vinculada',
  charge_unlinked: 'Cobrança desvinculada',
  subscription_linked: 'Assinatura vinculada',
  subscription_unlinked: 'Assinatura desvinculada',
  task_status_changed: 'Situação da tarefa alterada',
  task_phase_changed: 'Fase da tarefa alterada',
  // v1.1.0 — eventos replicados da Ordem de Serviço
  service_order_created: 'OS criada',
  service_order_schedule: 'OS agendada',
  service_order_status: 'Situação da OS alterada',
  service_order_phase: 'Fase da OS alterada',
  service_order_provider: 'Prestador da OS alterado',
  service_order_checklist_status: 'Checklist da OS atualizado',
};

/**
 * v1.1.0: detalhe dos eventos da OS (compartilhado com a timeline do Cliente).
 * `meta` é o payload gravado por tg_service_order_events_fanout.
 */
export function describeServiceOrderEvent(meta: Record<string, unknown> | null | undefined): string | null {
  const m = meta || {};
  const num = txt(m.number);
  switch (m.event_type) {
    case 'created':
      return m.service_type_name ? `${num} · ${m.service_type_name}` : num;
    case 'schedule': {
      const d = m.new_value ? new Date(String(m.new_value)) : null;
      return d && !isNaN(d.getTime())
        ? `${num} · ${d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}`
        : num;
    }
    case 'status':
      return `${num}: ${txt(m.old_value)} → ${txt(m.new_value)}${m.note ? ` · ${m.note}` : ''}`;
    case 'phase': {
      const [catOld, catNew, obs] = String(m.note ?? '').split('|');
      const fmt = (f: unknown, c?: string) => `"${txt(f)}"${c ? ` [${c}]` : ''}`;
      return `${num}: ${fmt(m.old_value, catOld)} → ${fmt(m.new_value, catNew)}${obs ? ` · ${obs}` : ''}`;
    }
    case 'provider':
      return `${num}: ${txt(m.old_value)} → ${txt(m.new_value)}`;
    case 'checklist_status':
      return `${num}: ${txt(m.new_value)}`;
    default:
      return num;
  }
}

/** Situações da vistoria em PT-BR. */
const INSPECTION_STATUS: Record<string, string> = {
  pending: 'Pendente',
  in_progress: 'Em andamento',
  awaiting_review: 'Aguardando revisão',
  in_correction: 'Em correção',
  correction_requested: 'Correção solicitada',
  approved: 'Aprovada',
  rejected: 'Reprovada',
  cancelled: 'Cancelada',
};

/** Situações da tarefa em PT-BR. */
const TASK_STATUS: Record<string, string> = {
  pending: 'Pendente',
  in_progress: 'Em andamento',
  paused: 'Pausada',
  completed: 'Concluída',
  cancelled: 'Cancelada',
};

const txt = (v: unknown) => (v === null || v === undefined || v === '' ? '—' : String(v));

/**
 * Texto de detalhe (linha "de → para" ou descrição) para os novos eventos.
 * Retorna null quando o evento não tem detalhe próprio.
 */
export function describeVehicleAuditEvent(
  action: string,
  metadata: Record<string, unknown> | undefined | null,
): string | null {
  const m = metadata || {};
  const origem = m.source === 'workflow' ? ' (fluxo automático)' : '';
  switch (action) {
    case 'vehicle_status_changed':
      return `${txt(m.previous_status_label ?? 'Sem status')} → ${txt(m.status_label ?? 'Sem status')}${origem}`;
    case 'inspection_created':
      return `Vistoria #${txt(m.code)}${m.template_name ? ` · ${m.template_name}` : ''}${origem}`;
    case 'inspection_status_changed':
      return `Vistoria #${txt(m.code)}: ${INSPECTION_STATUS[String(m.previous_status)] ?? txt(m.previous_status)} → ${
        INSPECTION_STATUS[String(m.status)] ?? txt(m.status)
      }${origem}`;
    case 'charge_linked':
    case 'charge_unlinked':
    case 'subscription_linked':
    case 'subscription_unlinked':
      return m.descricao ? String(m.descricao) : null;
    case 'task_status_changed':
      return `${TASK_STATUS[String(m.previous_status)] ?? txt(m.previous_status)} → ${
        TASK_STATUS[String(m.status)] ?? txt(m.status)
      }${origem}`;
    case 'task_phase_changed':
      return `${txt(m.previous_phase)} → ${txt(m.phase)}${origem}`;
    default:
      if (action.startsWith('service_order_')) return describeServiceOrderEvent(m);
      return null;
  }
}
